/**
 * Single source of truth for a match's home/away "analytics color" — the
 * possession rings, Match Momentum chart, Ball Recovery Time banner, and
 * Area Control grid all resolve their team color through this function so
 * the four surfaces can never drift from one another.
 */
import {
  resolveTeamPalette,
  TeamWithColors,
} from "../../../../lib/teamPalette";

export interface MatchTeamColors {
  home: string;
  away: string;
  /**
   * Raw `secondary_color` for each team (PMA-COCK-180), undefined when
   * absent — fed to `chartColor.ts`'s `resolveChartColor`/
   * `resolveChartTeamPair` as the chromatic fallback for an achromatic
   * `home`/`away` primary. Read directly off the team, independent of
   * `resolveTeamPalette`'s own `primary_color && secondary_color` gate
   * (PMA-COCK-055) — that gate decides whether the HASH palette or the
   * custom primary is used for `home`/`away` above; it has no bearing on
   * whether a supplied secondary is usable as a chart-color fallback.
   */
  homeSecondary?: string;
  awaySecondary?: string;
}

type MatchTeamWithKit = TeamWithColors & {
  kit?: { shirt_color?: string | null } | null;
};

interface MatchTeamsLike {
  home_team: MatchTeamWithKit;
  away_team: MatchTeamWithKit;
}

/**
 * EPIC-112 / D7: a match team's own kit shirt colour wins over the team's
 * catalog primary. Secondary is deliberately untouched.
 */
const resolveSidePrimary = (team: MatchTeamWithKit): string => {
  const shirt = team.kit?.shirt_color;
  return typeof shirt === "string" && shirt.trim()
    ? shirt
    : resolveTeamPalette(team).primary;
};

export function resolveMatchTeamColors(match: MatchTeamsLike): MatchTeamColors {
  return {
    home: resolveSidePrimary(match.home_team),
    away: resolveSidePrimary(match.away_team),
    homeSecondary: match.home_team.secondary_color ?? undefined,
    awaySecondary: match.away_team.secondary_color ?? undefined,
  };
}
